/**
 * useMobileTrainingOutcome — persists the outcome of an UNVERIFIED mobile
 * training launch when the user returns to OptiBay (unified with the desktop
 * completion options):
 *   - 'completed'  → "Yes, completed in full" (self-attested, LOW confidence)
 *   - 'partial'    → "Partially completed" (opened on mobile, not complete)
 *   - 'skipped'    → "Not relevant / skip" (no completion claim, no quiz)
 * ("I did not get to it yet" persists NOTHING — the prompt just clears the launch
 * record and leaves the training unrecorded, so it is neither completed nor partial.)
 *
 * INTEGRITY — writes ONLY a `training_consumption_sessions` row (client-writable)
 * plus, for a completion, the canonical analytics event. It NEVER writes:
 *   - extension-evidence rows (`training_session_extension_evidence`),
 *   - `provider_monitor` / `video_progress` / `media_playback` evidence,
 *   - companion-tab evidence.
 * A NEW session is inserted at `completion_confidence: 'unverified'` — the true
 * statement that no evidence exists yet. An UPDATE to an existing session does
 * NOT touch `completion_confidence` at all (CORE-AI Slice 1): a mobile
 * self-report is the person's own assertion and is not an adjudication, so it
 * may neither raise the value nor destroy a governed one. Resetting it to
 * 'unverified' used to be the defence against the desktop wall-clock heuristic
 * minting 'verified'; that writer is gone, and the only values reachable now
 * are governed ones — 'likely' from measured Companion evidence, 'verified'
 * from an audited manager attestation. Wiping either from a phone would be the
 * same ungoverned adjudication in the other direction, and the database refuses
 * it. `companion_verified` / `provider_verified` are recorded false; elapsed
 * time is stored as "time away" (never verified watch time) and never
 * auto-completes.
 *
 * DEDUPE — to avoid duplicate history rows across multiple mobile attempts on the
 * same artifact, an outcome UPDATES an existing non-completed mobile session for
 * (technician, artifact) when one exists (or the explicit `existingSessionId`
 * from a Resume), and only INSERTs when there is none. Elapsed/estimate reflect
 * the LATEST attempt (last-attempt semantics); an `attempts` counter is kept in
 * metadata for context.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { clock } from '@/utils/clock';
import { buildTrainingAttestation } from '@/lib/training/trainingCompletionOptions';
import { toJson } from '@/utils/toJson';

// DB completion_status enum already allows these (no migration needed). Only
// 'completed' triggers completion side-effects (attestation, confirmed_at,
// analytics event); 'partial'/'skipped' write a plain status passthrough.
export type MobileTrainingOutcome = 'completed' | 'partial' | 'skipped';

export interface MobileTrainingOutcomeInput {
  outcome: MobileTrainingOutcome;
  /** Resume target — UPDATE this exact session instead of find-or-insert. */
  existingSessionId?: string | null;
  artifactId: string;
  ticketId: string | null;
  technicianProfileId: string;
  providerUrl: string;
  elapsedSeconds: number;
  durationSeconds: number | null;
  estimatedCompletionPct: number | null;
}

const MOBILE_SOURCE = 'mobile_unverified_return';

export function useMobileTrainingOutcome() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: MobileTrainingOutcomeInput) => {
      const nowIso = clock.now().toISOString();
      const isCompleted = input.outcome === 'completed';

      // Locate an existing mobile session to update (dedupe): explicit resume id,
      // else the newest non-completed mobile session for this (tech, artifact).
      let target: { id: string; metadata: Record<string, unknown> | null } | null = null;
      if (input.existingSessionId) {
        const { data } = await supabase
          .from('training_consumption_sessions')
          .select('id, metadata')
          .eq('id', input.existingSessionId)
          .maybeSingle();
        if (data) target = data as typeof target;
      }
      if (!target) {
        const { data } = await supabase
          .from('training_consumption_sessions')
          .select('id, metadata')
          .eq('technician_profile_id', input.technicianProfileId)
          .eq('artifact_id', input.artifactId)
          .eq('source', MOBILE_SOURCE)
          .in('completion_status', ['partial', 'pending'])
          .order('opened_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (data) target = data as typeof target;
      }

      // WHO self-attested (completion only; best-effort).
      let manualMarkedCompleteBy: string | null = null;
      if (isCompleted) {
        try {
          const { data: authData } = await supabase.auth.getUser();
          manualMarkedCompleteBy = authData?.user?.id ?? null;
        } catch {
          manualMarkedCompleteBy = null;
        }
      }

      const priorMeta = (target?.metadata as Record<string, unknown> | null) ?? {};
      const priorEstimate =
        (priorMeta.mobileEstimate as Record<string, unknown> | undefined) ?? {};
      const priorAttempts =
        typeof priorEstimate.attempts === 'number' ? priorEstimate.attempts : 0;

      const mobileEstimate = {
        source: MOBILE_SOURCE,
        elapsed_seconds: input.elapsedSeconds,
        duration_seconds: input.durationSeconds,
        estimated_completion_pct: input.estimatedCompletionPct,
        provider_url: input.providerUrl,
        companion_verified: false,
        provider_verified: false,
        evidence_confidence: 'low' as const,
        attempts: priorAttempts + 1,
        last_attempt_at: nowIso,
      };

      // completion → neutral attestation snapshot. "Do it later" makes NO
      // completion claim, so we do NOT write a trainingAttestation for it.
      const metadata: Record<string, unknown> = { ...priorMeta, mobileEstimate };
      if (isCompleted) {
        metadata.trainingAttestation = buildTrainingAttestation({
          userReported: 'completed',
          monitoredPercent: null,
          confidence: 'low',
          evidenceUnavailable: true,
          at: nowIso,
        });
      }

      const expectedDurationMinutes =
        input.durationSeconds != null ? Math.round(input.durationSeconds / 60) : null;

      let sessionId: string | null = target?.id ?? null;

      if (target) {
        const update: Record<string, unknown> = {
          completion_status: input.outcome,
          // completion_confidence is deliberately ABSENT — see INTEGRITY above.
          opened_url_snapshot: input.providerUrl,
          ticket_id: input.ticketId,
          expected_duration_minutes: expectedDurationMinutes,
          updated_at: nowIso,
          metadata,
          confirmed_at: isCompleted ? nowIso : null,
          manual_marked_complete_at: isCompleted ? nowIso : null,
          manual_marked_complete_by: isCompleted ? manualMarkedCompleteBy : null,
          progress_percent: null,
        };
        const { error } = await supabase
          .from('training_consumption_sessions')
          .update(update)
          .eq('id', target.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('training_consumption_sessions')
          .insert({
            technician_profile_id: input.technicianProfileId,
            artifact_id: input.artifactId,
            ticket_id: input.ticketId,
            surface: 'ticket_sidebar',
            source: MOBILE_SOURCE,
            expected_duration_minutes: expectedDurationMinutes,
            completion_status: input.outcome,
            completion_confidence: 'unverified',
            confirmed_at: isCompleted ? nowIso : null,
            manual_marked_complete_at: isCompleted ? nowIso : null,
            manual_marked_complete_by: isCompleted ? manualMarkedCompleteBy : null,
            opened_url_snapshot: input.providerUrl,
            progress_percent: null,
            metadata: toJson(metadata),
          })
          .select()
          .single();
        if (error) throw error;
        sessionId = data?.id ?? null;
      }

      // Canonical analytics event ONLY for a completion (mirrors the desktop
      // confirm path). Honest: time_spent_seconds is NULL (time away, not watch).
      if (isCompleted) {
        await supabase.from('training_effectiveness_events').insert({
          technician_profile_id: input.technicianProfileId,
          artifact_id: input.artifactId,
          event_type: 'completed',
          source: MOBILE_SOURCE,
          surface: 'ticket_sidebar',
          time_spent_seconds: null,
          metadata: {
            session_id: sessionId,
            completion_confidence: 'unverified',
            source: MOBILE_SOURCE,
            elapsed_seconds: input.elapsedSeconds,
            estimated_completion_pct: input.estimatedCompletionPct,
            evidence_confidence: 'low',
            companion_verified: false,
            provider_verified: false,
          },
        });
      }

      return { sessionId, outcome: input.outcome };
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['consumption-sessions', variables.technicianProfileId],
      });
      queryClient.invalidateQueries({ queryKey: ['training-session-history'] });
    },
  });
}
